<template>
  <Dialog
    :model-value="dialogVisible"
    title="Send Document"
    dialog-type="primary"
    dialog-size="small"
    title-variant="medium"
    :header-icon-src="require('@/assets/MyPatient/document_white.svg')"
    :close-on-overlay="false"
    @update:model-value="(v) => { if (!v) closeDialog() }"
    @close="closeDialog"
  >
    <!-- Dialog Content -->
    <div class="send-document-body">
      <!-- Selection Info -->
      <Notification
        :message="`${selectedCount} document${selectedCount > 1 ? 's' : ''} selected.`"
        theme="info"
        variant="inline"
        :with_close_btn="false"
        class="mb-4"
      />

      <!-- Document Search -->
      <div class="mb-6 flex items-center gap-2">
        <div class="pl-3 pr-2">
          <img src="@/assets/MyPatient/search.svg" alt="Search" class="w-4 h-4 flex-shrink-0" />
        </div>
        <div class="flex-1 min-w-0">
          <TextInput
            v-model="searchQuery"
            size="medium"
            placeholder="Search Documents"
            :clearable="true"
          />
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="border-b bg-white border-[#E6E6E6] flex items-center px-2">
        <button
          class="tab-button flex-1 flex gap-1 items-center justify-center px-4 py-1 relative transition-all duration-200"
          :class="activeTab === 'form-templates' ? 'border-b-2 border-[#004879]' : ''"
          @click="switchTab('form-templates')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" class="tab-icon">
            <path d="M12.375 0.75H3.375C2.55 0.75 1.875 1.425 1.875 2.25V12.75H3.375V2.25H12.375V0.75ZM11.625 3.75H6.375C5.55 3.75 4.8825 4.425 4.8825 5.25L4.875 15.75C4.875 16.575 5.5425 17.25 6.3675 17.25H14.625C15.45 17.25 16.125 16.575 16.125 15.75V8.25L11.625 3.75ZM6.375 15.75V5.25H10.875V9H14.625V15.75H6.375Z" :fill="activeTab === 'form-templates' ? '#004879' : '#666666'"/>
          </svg>
          <span 
            class="tab-text font-normal text-[14px] leading-5 tracking-[0.1px] text-center whitespace-nowrap transition-colors duration-200"
            :class="activeTab === 'form-templates' ? 'text-[#004879]' : 'text-[#999999]'"
            style="font-family: 'Inter', sans-serif;"
          >
            Form Templates
          </span>
        </button>
        <button
          class="tab-button flex-1 flex gap-1 items-center justify-center px-4 py-1 relative transition-all duration-200"
          :class="activeTab === 'documents' ? 'border-b-2 border-[#004879]' : ''"
          @click="switchTab('documents')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" class="tab-icon">
            <path d="M11.625 4.5V12.375C11.625 14.0325 10.2825 15.375 8.625 15.375C6.9675 15.375 5.625 14.0325 5.625 12.375V4.5C5.625 3.465 6.465 2.625 7.5 2.625C8.535 2.625 9.375 3.465 9.375 4.5V11.625C9.375 12.0375 9.0375 12.375 8.625 12.375C8.2125 12.375 7.875 12.0375 7.875 11.625V4.5H6.75V11.625C6.75 12.66 7.59 13.5 8.625 13.5C9.66 13.5 10.5 12.66 10.5 11.625V4.5C10.5 2.8425 9.1575 1.5 7.5 1.5C5.8425 1.5 4.5 2.8425 4.5 4.5V12.375C4.5 14.655 6.345 16.5 8.625 16.5C10.905 16.5 12.75 14.655 12.75 12.375V4.5H11.625Z" :fill="activeTab === 'documents' ? '#004879' : '#1F1F1F'"/>
          </svg>
          <span 
            class="tab-text font-normal text-[14px] leading-5 tracking-[0.1px] text-center whitespace-nowrap transition-colors duration-200"
            :class="activeTab === 'documents' ? 'text-[#004879]' : 'text-[#999999]'"
            style="font-family: 'Inter', sans-serif;"
          >
            Documents
          </span>
        </button>
      </div>

      <!-- Document Table -->
      <div class="document-table py-2">
        <div class="table-body">
          <template v-if="filteredDocuments.length > 0">
            <div
              v-for="document in paginatedDocuments"
              :key="document.id || document.accessionId" 
              class="table-row"
            >
              <div class="row-checkbox w-[5%]">
                <Checkbox
                  :model-value="!!document.selected"
                  built_in_theme="primary-blue"
                  @change="(checked) => toggleDocument(document, checked)"
                />
              </div>
              <div class="row-name truncate" :class="activeTab === 'form-templates' ? 'w-[90%]' : 'w-[22%]'">{{ getDocumentName(document) }}</div>
              <div v-if="activeTab !== 'form-templates'" class="row-type w-[38%]">{{ getDocumentType(document) }}</div>
              <div v-if="activeTab !== 'form-templates'" class="row-timestamp w-[30%]">{{ formatTimestamp(document.createdAt) }}</div>
              <div class="row-actions w-[5%] flex justify-center">
                <button 
                  v-if="document.type === 'uploaded_file'"
                  class="delete-btn rounded hover:bg-gray-100 transition-colors"
                  :disabled="document.isDeleting"
                  @click="deleteDocument(document)"
                >
                  <img 
                    src="@/assets/MyPatient/delete_doc.svg" 
                    alt="Delete" 
                    class=""
                    :class="{ 'opacity-50': document.isDeleting }"
                  />
                </button>
              </div>
            </div>
          </template>
          <div v-else class="w-full h-[160px] flex flex-col items-center justify-center">
            <img src="@/assets/MyPatient/no_doc.svg" alt="No documents" class="mb-2"/>
            <div class="">There is no file for this patient yet.</div>
          </div>
        </div>
      </div>

      <!-- Pagination for Form Templates -->
      <div v-if="activeTab === 'form-templates'" class="mb-6">
        <Pagination
          :total_items="totalFormTemplates"
          :current_page="formTemplatesCurrentPage"
          :per_page="formTemplatesPageSize"
          :show_summary="false"
          item_label="form templates"
          @update:current_page="handleFormTemplatesPageChange"
          @update:per_page="handleFormTemplatesPageSizeChange"
        />
      </div>

      <!-- Pagination for Documents -->
      <div v-else class="mb-6">
        <Pagination
          :total_items="filteredDocuments.length"
          :current_page="documentsCurrentPage"
          :per_page="documentsPageSize"
          :show_summary="false"
          item_label="documents"
          @update:current_page="handleDocumentsPageChange"
          @update:per_page="handleDocumentsPageSizeChange"
        />
      </div>

      <!-- Upload Files Section -->
      <FileUploader
        v-model:files="uploadedFiles"
        title="Upload files"
        description="Max file size is 10MB."
        drop_hint="Drag and drop your files here to add,"
        button_text="Add File"
        :accept="acceptedTypes"
        :allowed_extensions="allowedExtensions"
        :max_size="maxFileSize"
        :multiple="true"
        @add="handleFileUploaderAdd"
        @reject="handleFileUploaderReject"
      />
    </div>

    <!-- Dialog Footer -->
    <template #footer>
      <div class="send-document-footer-leading">
        <ResponsiveButton
          button_type="outlined"
          built_in_theme="error"
          size="medium"
          display_name="Cancel"
          @click-button="closeDialog"
        />
      </div>
      <div class="send-document-footer-actions">
        <ResponsiveButton
          button_type="filled"
          built_in_theme="dark"
          size="medium"
          display_name="Send"
          :disabled="!hasSelection"
          @click-button="sendDocuments"
        />
      </div>
    </template>
  </Dialog>
  
  <!-- Confirmation Dialog -->
  <ConfirmSendDocumentsDialog
    :visible="showConfirmDialog"
    :documents="documentsToConfirm"
    @close="showConfirmDialog = false"
    @confirm="confirmAndSend"
  />
</template>

<script>
import { uploadPatientFileApi, createPatientFileFormData, getPatientFilesApi, deletePatientFileApi } from '@/api/patientFiles'
import { sendMessageApi } from '@/api/rocketChat'
import { getFormsApi, sendFormApi, getPatientCompletedFormsApi } from '@/api/documents'
import ConfirmSendDocumentsDialog from './ConfirmSendDocumentsDialog.vue';
import { notify } from '@/utils/notify';

export default {
  name: 'SendDocumentDialog',
  components: {
    ConfirmSendDocumentsDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    roomId: {
      type: [String, null],
      required: true
    },
    clinicId: {
      type: String,
      required: true
    },
    patientId: {
      type: [String, null],
      required: true
    },
    source: {
      type: String,
      default: 'chat'
    },
    patientInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      activeTab: 'documents', // 'form-templates' or 'documents'
      searchQuery: '',
      selectAll: false,
      selectedCount: 0,
      // Separate pagination for each tab
      formTemplatesCurrentPage: 1,
      formTemplatesPageSize: 5,
      documentsCurrentPage: 1,
      documentsPageSize: 5,
      uploadedFiles: [],
      acceptedTypes: '.pdf,.doc,.docx,.csv,.xlsx,.xls,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.bmp,.webp,.tiff,.svg,.zip,.rar,.7z',
      allowedExtensions: ['pdf','doc','docx','csv','xlsx','xls','ppt','pptx','png','jpg','jpeg','gif','bmp','webp','tiff','svg','zip','rar','7z'],
      maxFileSize: 10 * 1024 * 1024, // 10MB
      documents: [], // Will be populated from fetchPatientFiles() API call
      completedForms: [], // Will be populated from getPatientCompletedFormsApi() call
      formTemplates: [], // Will be populated from getFormsApi() call
      formAssignmentData: null, // Store form assignment data from send API
      totalFormTemplates: 0, // Total count from API for form templates pagination
      searchDebounceTimer: null,
      showConfirmDialog: false,
      documentsToConfirm: []
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    filteredDocuments() {
      let filtered = [];
      
      // Step 1: Filter by active tab
      if (this.activeTab === 'documents') {
        filtered = this.documents.filter(doc => 
          doc.type === 'uploaded_file' || doc.type === 'report'
        );
      } else if (this.activeTab === 'completed-forms') {
        filtered = this.completedForms;
      } else if (this.activeTab === 'form-templates') {
        // Form templates are already filtered by API
        return this.formTemplates;
      }
      
      // Step 2: Apply local search filter for documents and completed-forms tabs
      if (this.searchQuery && this.searchQuery.trim() && this.activeTab !== 'form-templates') {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(doc => {
          const name = (doc.name || doc.fileName || doc.title || '').toLowerCase();
          const description = (doc.description || '').toLowerCase();
          return name.includes(query) || description.includes(query);
        });
      }
      
      return filtered;
    },
    paginatedDocuments() {
      // For form templates, data is already paginated from API
      if (this.activeTab === 'form-templates') {
        return this.filteredDocuments;
      }
      // For documents tab, use client-side pagination
      const startIndex = (this.documentsCurrentPage - 1) * this.documentsPageSize;
      const endIndex = startIndex + this.documentsPageSize;
      return this.filteredDocuments.slice(startIndex, endIndex);
    },
    totalDocuments() {
      // For form templates, use the total from API, otherwise use filtered length
      if (this.activeTab === 'form-templates') {
        return this.totalFormTemplates;
      }
      return this.filteredDocuments.length;
    },
    totalPages() {
      return Math.ceil(this.totalDocuments / this.pageSize);
    },
    hasSelection() {
      return this.selectedCount > 0 || this.uploadedFiles.length > 0;
    }
  },
  
  watch: {
    searchQuery() {
      // Only use debounced API search for form templates
      if (this.activeTab === 'form-templates') {
        // Clear existing debounce timer
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
        }
        
        // Debounce search to avoid excessive API calls
        this.searchDebounceTimer = setTimeout(() => {
          this.formTemplatesCurrentPage = 1;
          this.fetchFormTemplates();
        }, 500);
      } else {
        // For documents and completed-forms, reset to page 1 (local filtering)
        this.documentsCurrentPage = 1;
      }
    },
    async visible(newVal) {
      if (newVal && this.clinicId && this.patientId) {
        // Fetch patient files first, then completed forms to avoid race condition
        await this.fetchPatientFiles();
        await this.fetchCompletedForms();
        this.fetchFormTemplates(); // Can run independently
      }
    },
    activeTab(newTab) {
      // Reset search when switching tabs (keep pagination separate)
      this.searchQuery = '';
      
      // Refetch form templates when switching to that tab to ensure data matches current page size
      if (newTab === 'form-templates') {
        this.fetchFormTemplates();
      }
    },
    // Refetch form templates when page changes
    formTemplatesCurrentPage(newPage, oldPage) {
      if (newPage !== oldPage && this.activeTab === 'form-templates') {
        this.fetchFormTemplates();
      }
    }
  },
  beforeUnmount() {
    // Clear debounce timer on component cleanup
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  },
  methods: {
    switchTab(tabName) {
      // Only switch if it's a different tab
      if (this.activeTab !== tabName) {
        this.activeTab = tabName;
        this.currentPage = 1;
        this.searchQuery = '';
      }
    },
    async fetchPatientFiles() {
      try {
        console.log('🔍 Fetching patient files');
        const response = await getPatientFilesApi(this.clinicId, this.patientId);
        console.log('📁 Patient files response received');
        
        if (response.data && Array.isArray(response.data)) {
          // Process and transform the API response into our expected format
          this.documents = response.data.map(item => {
            // Add selected property for UI state management
            const processedItem = { ...item, selected: false };
            
            // Determine type based on data structure
            if (item.fileName || item.fileSize || item.contentType) {
              // It's an uploaded file
              processedItem.type = 'uploaded_file';
            } else if (item.accessionId) {
              // It's a report
              processedItem.type = 'report';
            }
            
            return processedItem;
          });
          
          console.log(`✅ Processed ${this.documents.length} documents:`, this.documents);
        } else {
          console.warn(`⚠️ Unexpected response format:`, response.data);
          this.documents = [];
        }
        
      } catch (error) {
        console.error(`❌ Error fetching room files:`, error);
        if (error.response) {
          console.error(`Response status:`, error.response.status);
          console.error(`Response data:`, error.response.data);
        }
        
        // Reset to empty array on error
        this.documents = [];
        notify({
          title: 'Error',
          message: 'Failed to load uploaded files',
          type: 'error'
        });
      }
    },
    
    async fetchCompletedForms() {
      try {
        console.log('🔍 Fetching completed forms');
        const response = await getPatientCompletedFormsApi(this.patientId, { limit: 100, offset: 0 });
        console.log('📋 Completed forms response received');
        
        // API returns data in response.data.assignments array
        if (response.data && response.data.assignments && Array.isArray(response.data.assignments)) {
          // Flatten the assignments array and transform to our document format
          this.completedForms = response.data.assignments.map(assignment => ({
            id: assignment.id,
            type: 'returned_form',
            fileName: assignment.form?.name || 'Completed Form',
            title: assignment.form?.name || 'Completed Form',
            category: assignment.form?.category || '',
            formId: assignment.form?.id || '',
            formVersion: assignment.form?.version || 1,
            externalUrl: assignment.link, // This is the doc_url we'll use for preview
            createdAt: assignment.created_at,
            completedAt: assignment.completed_at,
            selected: false,
            // Add metadata for display
            fileSize: 0, // Completed forms don't have file size
            contentType: 'application/pdf' // Assume PDF for display purposes
          }));
          
          // Merge completed forms into documents array
          this.documents = [...this.documents, ...this.completedForms];
          
          console.log(`✅ Loaded ${this.completedForms.length} completed forms, total documents: ${this.documents.length}`);
        } else {
          console.warn('⚠️ Unexpected completed forms response format:', response.data);
          this.completedForms = [];
        }
      } catch (error) {
        console.error('❌ Error fetching completed forms:', error);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        notify({
          title: 'Error',
          message: 'Failed to load completed forms',
          type: 'error'
        });
        this.completedForms = [];
      }
    },
    
    async fetchFormTemplates() {
      try {
        // Calculate offset based on current page and page size for form templates
        const offset = (this.formTemplatesCurrentPage - 1) * this.formTemplatesPageSize;
        
        console.log(`🔍 Fetching form templates from /v1/ehr/file API (limit: ${this.formTemplatesPageSize}, offset: ${offset})`);
        const params = {
          limit: this.formTemplatesPageSize, 
          offset: offset
        };
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        const response = await getFormsApi(params);
        console.log('📋 Form templates response:', response.data);
        
        // API returns data in response.data with total, limit, offset, and items
        if (response.data && response.data.items && Array.isArray(response.data.items)) {
          // Store total count from API for pagination
          this.totalFormTemplates = response.data.total || 0;
          
          // Add selected property for UI state management
          this.formTemplates = response.data.items.map(form => ({
            ...form,
            selected: false,
            type: 'form_template' // Add type for consistency
          }));
          
          console.log(`✅ Loaded ${this.formTemplates.length} form templates (total: ${this.totalFormTemplates})`);
        } else {
          console.warn('⚠️ Unexpected form templates response format:', response.data);
          this.formTemplates = [];
          this.totalFormTemplates = 0;
        }
      } catch (error) {
        console.error('❌ Error fetching form templates:', error);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        notify({
          title: 'Error',
          message: 'Failed to load form templates',
          type: 'error'
        });
        this.formTemplates = [];
        this.totalFormTemplates = 0;
      }
    },
    
    closeDialog() {
      this.resetForm();
      this.$emit('close');
    },
    async sendDocuments() {
      if (!this.hasSelection) {
        return;
      }
      
      // Only collect selected documents from 'documents' tab for confirmation
      const selectedDocs = this.documents.filter(doc => doc.selected);
      
      // Only show confirmation dialog if there are selected documents from 'documents' tab
      if (selectedDocs.length > 0) {
        this.documentsToConfirm = selectedDocs.map(doc => ({
          name: doc.fileName || doc.name || doc.title || 'Untitled Document',
          type: this.getDocumentType(doc),
          createdAt: doc.createdAt
        }));
        this.showConfirmDialog = true;
      } else {
        // If only form templates selected, send directly without confirmation
        this.confirmAndSend();
      }
    },
    
    async confirmAndSend() {
      this.showConfirmDialog = false;
      const selectedDocuments = this.documents.filter(doc => doc.selected)
        .concat(this.formTemplates.filter(form => form.selected));

      if (this.source !== 'chat') {
        this.$emit('send', selectedDocuments);
        this.resetForm();
        this.$emit('close');
        return;
      }

      try {
        console.log('📤 Sending documents:', selectedDocuments);

        // Send all documents concurrently as separate messages
        await Promise.all(selectedDocuments.map(document => this.sendSingleDocument(document)));

        console.log('✅ All documents sent successfully!');
        notify({
          title: 'Success',
          message: `${selectedDocuments.length} document(s) sent successfully!`,
          type: 'success'
        });

        // Reset form and close dialog
        this.resetForm();
        this.$emit('close');

      } catch (error) {
        console.error('❌ Error sending documents:', error);
        notify({
          title: 'Error',
          message: 'Failed to send documents. Please try again.',
          type: 'error'
        });
      } finally {
        this.showConfirmDialog = false;
      }
    },
    
    async sendSingleDocument(document) {
      // Reset form assignment data for each document
      this.formAssignmentData = null;
      
      if (document.type === 'form_template') {
        // Form template - ONLY call send API, no message sending
        try {
          console.log('📋 Sending form template:', document.id);
          const sendResponse = await sendFormApi(document.id, {
            patient_id: parseInt(this.patientId)
          });
          
          console.log('✅ Form template sent successfully:', sendResponse.data);
          return sendResponse.data;
        } catch (error) {
          console.error('❌ Failed to send form template:', error);
          throw error;
        }
      }
      
      // For other document types (uploaded_file, returned_form, report), send message as before
      let attachmentTitle, idFieldTitle, idFieldValue, documentName, createdAt, fileType, fileSize, docUrl;
      
      if (document.type === 'returned_form') {
        // Returned form - backend structure: {id, fileName, fileSize, contentType, createdAt, type, externalUrl}
        attachmentTitle = 'doc_card_returned_form';
        idFieldTitle = 'id';
        idFieldValue = document.id;
        documentName = document.fileName || 'Returned Form';
        createdAt = document.createdAt;
        fileType = this.getFileType(document);
        fileSize = this.formatFileSize(document.fileSize || 0);
        docUrl = document.externalUrl || ''; // URL for returned form
      } else if (document.id) {
        // Uploaded document - backend structure: {id, fileName, fileSize, contentType, createdAt, type}
        attachmentTitle = 'doc_card_uploaded';
        idFieldTitle = 'id';
        idFieldValue = document.id; // Backend uses 'id' field
        documentName = document.fileName; // Backend uses 'fileName' field
        createdAt = document.createdAt; // Backend uses 'createdAt' field (ISO string)
        fileType = this.getFileType(document); // Will use 'contentType' field
        fileSize = this.formatFileSize(document.fileSize || 0); // Backend uses 'fileSize' field
        docUrl = null; // No URL for uploaded files
      } else {
        // Report document  
        attachmentTitle = 'doc_card_report';
        idFieldTitle = 'accessionId';
        idFieldValue = document.accessionId;
        documentName = document.fileName || document.name || `Report ${document.accessionId}`;
        createdAt = document.createdAt || document.uploadedAt || new Date();
        fileType = 'PDF'; // Reports are always PDF
        fileSize = '0 B'; // Reports have 0 size as specified
        docUrl = null; // No URL for reports
      }
      
      // Format created date to readable string
      const formattedCreatedAt = createdAt instanceof Date ? 
        createdAt.toISOString() : 
        new Date(createdAt).toISOString();
      
      // Prepare fields array
      const fields = [
        { "title": idFieldTitle, "value": idFieldValue },
        { "title": "name", "value": documentName },
        { "title": "created", "value": formattedCreatedAt },
        { "title": "ftype", "value": fileType },
        { "title": "size", "value": fileSize }
      ];
      
      // Add doc_url field for returned forms
      if (docUrl) {
        fields.push({ "title": "doc_url", "value": docUrl });
      }
      
      // Prepare message body for uploaded documents, returned forms, and reports
      const messageBody = {
        "message": {
          "rid": this.roomId,
          "msg": "", // Empty message text for document cards
          "alias": "",
          "t": "doc_card",
          "emoji": "",
          "avatar": "",
          "attachments": [
            {
              "text": "This is a document card message",
              "title": attachmentTitle,
              "fields": fields
            }
          ]
        }
      };
      
      console.log('📤 Sending single document message:', messageBody);
      
      // Send message via API
      const response = await sendMessageApi(messageBody.message);
      
      if (response.data && response.data.success) {
        const responseMessage = response.data.message;
        console.log('✅ Document message sent successfully:', responseMessage);
        
        // Add to Vuex store if available
        if (this.$store) {
          const messageId = responseMessage._id;
          const roomId = responseMessage.rid;
          const existingSentMessage = this.$store.getters['myPatients/message/getSentMessage'](roomId, messageId);
          
          // Add to sent messages first
          if (!existingSentMessage) {
            this.$store.dispatch('myPatients/message/addSentMessage', {
              roomId: roomId,
              messageId: messageId,
              message: responseMessage
            });
            this.$store.dispatch('myPatients/message/addMessage', {
            roomId: roomId,
            message: responseMessage
          });
          }
        }
        
        return responseMessage;
      } else {
        throw new Error('Failed to send document');
      }
    },
    handleSelectAll() {
      this.selectAll = !this.selectAll;
      this.filteredDocuments.forEach(doc => {
        doc.selected = this.selectAll;
      });
      this.updateSelectionCount();
    },
    toggleDocument(document, checked) {
      document.selected = !!checked;
      this.updateSelectionCount();
    },
    updateSelectionCount() {
      // Count selected documents from both documents and form templates
      const selectedDocs = this.documents.filter(doc => doc.selected).length;
      const selectedForms = this.formTemplates.filter(form => form.selected).length;
      this.selectedCount = selectedDocs + selectedForms;
      
      // Update selectAll based on current tab's filtered documents only
      const selectedInCurrentTab = this.filteredDocuments.filter(doc => doc.selected).length;
      this.selectAll = selectedInCurrentTab === this.filteredDocuments.length && this.filteredDocuments.length > 0;
    },
    getDocumentName(document) {
      if (document.type === 'form_template') {
        return document.name || document.title || 'Untitled Form';
      }
      return document.id ? document.fileName : document.accessionId;
    },
    getDocumentType(document) {
      if (document.type === 'uploaded_file') {
        return 'Uploaded Documents';
      } else if (document.type === 'report') {
        return 'Reports';
      } else if (document.type === 'returned_form') {
        return 'Returned Form';
      } else if (document.type === 'form_template') {
        return 'Form Template';
      }
      return 'Unknown';
    },
    formatTimestamp(createdAt) {
      const date = new Date(createdAt);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    // Form Templates pagination handlers
    handleFormTemplatesPageChange(page) {
      this.formTemplatesCurrentPage = page;
    },
    handleFormTemplatesPageSizeChange(newPageSize) {
      this.formTemplatesPageSize = newPageSize;
      this.formTemplatesCurrentPage = 1; // Reset to first page when page size changes
      this.fetchFormTemplates(); // Refetch with new page size
    },
    
    // Documents pagination handlers
    handleDocumentsPageChange(page) {
      this.documentsCurrentPage = page;
    },
    handleDocumentsPageSizeChange(newPageSize) {
      this.documentsPageSize = newPageSize;
      this.documentsCurrentPage = 1; // Reset to first page when page size changes
    },

    // ===== FileUploader handlers =====
    handleFileUploaderAdd(newItems) {
      newItems.forEach((item) => this.uploadItem(item));
    },
    handleFileUploaderReject(rejected) {
      rejected.forEach(({ file, reason }) => {
        notify({
          title: 'Error',
          message: `${file.name}: ${reason}`,
          type: 'error',
        });
      });
    },
    async uploadItem(item) {
      // Validate required IDs before creating FormData
      if (!this.clinicId || !this.patientId) {
        item.status = 'failed';
        item.error = 'Missing clinic or patient ID';
        notify({ title: 'Error', message: item.error, type: 'error' });
        return;
      }

      // Fake progress animation while we wait on the server
      const progressInterval = setInterval(() => {
        if (item.status !== 'uploading') return;
        const current = item.progress || 0;
        if (current < 90) {
          const inc = Math.random() * 15 + 5;
          item.progress = Math.min(Math.round(current + inc), 90);
        }
      }, 300);

      try {
        const formData = createPatientFileFormData({
          clinicId: String(this.clinicId),
          patientId: String(this.patientId),
          file: item.raw,
          source: 'provider_upload',
        });
        const response = await uploadPatientFileApi(formData);

        clearInterval(progressInterval);
        item.progress = 100;
        item.status = 'success';

        // Brief delay to display 100%, then push into the documents table
        await new Promise((resolve) => setTimeout(resolve, 400));

        const documentItem = {
          ...response.data,
          fileName: item.name,
          fileSize: item.size,
          contentType: item.raw?.type || '',
          type: 'uploaded_file',
          selected: true,
          uploadedAt: new Date(),
          originalFile: item.raw,
        };
        this.documents.unshift(documentItem);
        this.updateSelectionCount();

        // Remove the file row from the uploader once it's been moved to the table
        const idx = this.uploadedFiles.findIndex((f) => f.id === item.id);
        if (idx !== -1) this.uploadedFiles.splice(idx, 1);

        notify({
          title: 'Success',
          message: `File "${item.name}" uploaded successfully!`,
          type: 'success',
        });
      } catch (error) {
        clearInterval(progressInterval);
        let errorDetails = error?.message || 'Upload failed';
        if (error?.response) {
          const status = error.response.status;
          if (status === 403) errorDetails = 'Access denied. Please check your permissions.';
          else if (status === 401) errorDetails = 'Authentication required. Please log in again.';
          else if (status === 413) errorDetails = 'File size exceeds server limit.';
          if (error.response.data?.message) errorDetails = error.response.data.message;
        }
        item.status = 'failed';
        item.error = errorDetails;
        notify({
          title: 'Error',
          message: `Failed to upload "${item.name}": ${errorDetails}`,
          type: 'error',
        });
      }
    },

    /**
     * Delete an uploaded file from the server using the file ID
     * @param {string} fileId - The ID of the file to delete
     * @returns {Promise<boolean>} - Returns true if deletion was successful, false otherwise
     */
    /**
     * Delete a document from the table (only for uploaded files)
     */
    async deleteDocument(document) {
      if (document.type !== 'uploaded_file') {
        console.warn('🚫 Cannot delete non-uploaded file');
        return;
      }
      
      try {
        const fileId = document.id;
        const fileName = document.fileName;
        
        console.log(`🗑️ Deleting document: ${fileName} (ID: ${fileId})`);
        
        // Call the patient files delete API
        const response = await deletePatientFileApi(fileId);
        
        console.log('🔍 Delete API response:', response);
        
        // If we get here without error, deletion was successful (status 200)
        // Remove document from the documents array
        const index = this.documents.findIndex(doc => doc.id === fileId);
        if (index !== -1) {
          this.documents.splice(index, 1);
        }
        
        // Update selected count
        this.selectedCount = this.documents.filter(doc => doc.selected).length;
        
        // Show success message
        notify({
          title: 'Success',
          message: `Document "${fileName}" deleted successfully!`,
          type: 'success'
        });
        
        console.log(`✅ Document "${fileName}" removed from list`);
      } catch (error) {
        console.error('❌ Error in deleteDocument:', error);
        notify({
          title: 'Error',
          message: `Failed to delete document "${document.fileName}"`,
          type: 'error'
        });
      }
    },
    
    resetForm() {
      this.searchQuery = '';
      this.documentTypeFilter = '';
      this.timeRangeFilter = '';
      this.selectAll = false;
      this.selectedCount = 0;
      this.currentPage = 1;
      this.uploadedFiles = [];
      this.showConfirmDialog = false;
      this.documentsToConfirm = [];

      this.documents.forEach(doc => {
        doc.selected = false;
      });
      this.formTemplates.forEach(form => {
        form.selected = false;
      });
    },
    getFileType(file) {
      // Handle different file object structures
      const fileType = file.contentType || '';
      
      // If no MIME type found, try to determine from filename extension
      if (!fileType) {
        const fileName = file.fileName || file.name || '';
        
        if (fileName) {
          const extension = fileName.split('.').pop()?.toLowerCase();
          
          switch (extension) {
            case 'pdf': return 'PDF';
            case 'doc': case 'docx': return 'Document';
            case 'xls': case 'xlsx': case 'csv': return 'Spreadsheet';
            case 'ppt': case 'pptx': return 'Presentation';
            case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': return 'Image';
            case 'zip': case 'rar': case '7z': return 'Archive';
            case 'txt': return 'Text';
            default: return 'File';
          }
        }
      }
      
      // Image files
      if (fileType.startsWith('image/')) return 'Image';
      
      // PDF files
      if (fileType === 'application/pdf') return 'PDF';
      
      // Word documents
      if (fileType === 'application/msword' || 
          fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        return 'Document';
      }
      
      // Excel files
      if (fileType === 'text/csv' || fileType === 'application/csv' ||
          fileType === 'application/vnd.ms-excel' ||
          fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        return 'Spreadsheet';
      }
      
      // PowerPoint files
      if (fileType === 'application/vnd.ms-powerpoint' ||
          fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
        return 'Presentation';
      }
      
      // Archive files
      if (fileType === 'application/zip' ||
          fileType === 'application/x-zip-compressed' ||
          fileType === 'application/x-rar-compressed' ||
          fileType === 'application/x-7z-compressed') {
        return 'Archive';
      }
      
      return 'File';
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },
    getFileExtension(filename) {
      return filename.split('.').pop().toLowerCase();
    }
  }
}
</script>

<style>
.send-document-dialog .el-overlay-dialog {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px;
}

.send-document-dialog .el-dialog {
  border-radius: 10px;
  margin: 0 auto;
  --el-dialog-padding-primary: 0px !important;
}

/* Max height dialog styling */
.send-doucment-max-height-dialog .el-dialog {
  max-height: 80vh !important;
  display: flex;
  flex-direction: column;
}

.send-doucment-max-height-dialog .el-dialog__body {
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Scrollable content container */
.dialog-scrollable-content {
  max-height: calc(80vh - 120px); /* Subtract header and footer height */
  overflow-y: auto;
  flex: 1;
}
.send-document-dialog.el-dialog__header {
  padding: 0;
  margin: 0;
}

.send-document-dialog .el-dialog__body {
  padding: 0;
}

.send-document-dialog .el-dialog__footer {
  padding: 0;
}

.send-document-dialog {
  border-radius: 12px;
  overflow: hidden;
}
</style>

<style scoped>
.search-input {
  padding-left: 40px;
}

.filter-select {
  width: 160px;
}

.document-table {
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  border-bottom: 1px dashed var(--DividerGray, #C4C4C4);
  background: #FFF;
  overflow: hidden;
  margin-bottom: 16px;
}

.table-header {
  display: grid;
  grid-template-columns: 40px 1fr 150px 150px;
  gap: 16px;
  padding: 12px 16px;
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #666666;
}

.table-body {
  max-height: 200px;
  overflow-y: auto;
}

.table-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding:4px 16px;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #333333;
  min-height: auto;
}

.table-row:last-child {
  border-bottom: none;
}

/* Table row content styles */
.row-checkbox {
  display: flex;
  align-items: center;

}

.row-name {
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  padding-right: 8px;
}

.row-type {
  word-wrap: break-word;
}

.row-timestamp {
  word-wrap: break-word;
  font-size: 13px;
  color: #666;
}

.page-size-select {
  width: 120px;
}

.upload-area {
  border: 2px dashed var(--DividerGray, #C4C4C4);
  border-radius: 8px;
  padding: 40px 20px;
  background: var(--Gray-White, #F2F2F2);
  text-align: center;
  transition: border-color 0.2s ease;
}

.upload-area:hover {
  border-color: #1890ff;
}

.upload-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.upload-text, .upload-or {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #979797;
}


.uploaded-files {
  margin-top: 16px;
}

.uploaded-file {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #f2f2f2;
  margin-bottom: 8px;
  width: 420px;
}

.file-name {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #333333;
}

.remove-file-btn {
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;
}

.remove-file-btn:hover {
  background-color: #e6e6e6;
}

.remove-file-btn img {
  width: 16px;
  height: 16px;
}

.cancel-btn {
  width: 120px;
  height: 40px;
  border: 1px solid #d10000;
  color: #d10000;
  background-color: white;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-weight: 400;
  font-size: 14px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cancel-btn:hover {
  border-color: #d10000;
  color: #d10000;
}

.send-btn {
  min-width: 120px;
  height: 40px;
  background-color: #b0b0b0;
  border-color: #b0b0b0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.send-btn:not(:disabled) {
  background-color: #004879;
  border-color: #004879;
}

.send-btn:not(:disabled):hover {
  background-color: #0066a3;
  border-color: #0066a3;
}

/* Custom Element Plus styling */
:deep(.el-input .el-input__wrapper) {
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  min-height: 32px;
  box-shadow: none;
}

:deep(.el-input .el-input__wrapper:hover) {
  border-color: #1890ff;
}

:deep(.el-input .el-input__wrapper.is-focus) {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

:deep(.el-select .el-input .el-input__wrapper) {
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  min-height: 32px;
  box-shadow: none;
}

:deep(.el-select .el-input .el-input__wrapper:hover) {
  border-color: #1890ff;
}

:deep(.el-select .el-input .el-input__wrapper.is-focus) {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* Tab button hover styles */
.tab-button:not(.border-b-2):hover {
  border-bottom: 2px solid #81B0E1;
}

.tab-button:not(.border-b-2):hover .tab-text {
  color: #81B0E1 !important;
}

.tab-button:not(.border-b-2):hover .tab-icon path {
  fill: #81B0E1;
}

:deep(.el-input__inner) {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  color: #4a4a4a;
}

:deep(.el-select .el-input__inner) {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  color: #4a4a4a;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .search-filters {
    flex-direction: column;
  }
  
  .filter-container {
    justify-content: flex-start;
  }
  
  .table-header, .table-row {
    grid-template-columns: 40px 1fr;
    grid-template-rows: auto auto;
  }
  
  .header-type, .header-timestamp,
  .row-type, .row-timestamp {
    grid-column: 2;
    font-size: 12px;
    color: #999999;
  }
}
.footer-dasher {
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
}

.send-document-body {
  width: 100%;
  padding: 24px 0;
}

/* Stretch the library FileUploader to the full body width */
.send-document-body :deep(.va-file-uploader),
.send-document-body :deep(.va-file-uploader__drop),
.send-document-body :deep(.va-file-uploader__file-list) {
  width: 100%;
  max-width: 100%;
}

.send-document-footer-leading {
  display: flex;
  align-items: center;
}

.send-document-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-left: auto;
}

</style>
